import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ListingService from '../services/ListingService';
import AreaGuideModal from './AreaGuideModal';

const AllListingsPage = ({ areas, agent, onGoBack, onSelectProperty }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get('page') || '1', 10) - 1;
  const currentPage = pageFromUrl >= 0 ? pageFromUrl : 0;

  const [selectedArea, setSelectedArea] = useState('');
  const [listings, setListings] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(currentPage);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showAreaGuideModal, setShowAreaGuideModal] = useState(false);

  // Filters State
  const initialKeyword = searchParams.get('keyword') || searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(initialKeyword);
  const [activeKeyword, setActiveKeyword] = useState(initialKeyword);
  const [propertyStatus, setPropertyStatus] = useState('FOR SALE');
  const [propertyType, setPropertyType] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [sortBy, setSortBy] = useState('latest');

  // Trigger search on mount or when filter criteria or page searchParam changes
  useEffect(() => {
    fetchProperties(currentPage, false);
  }, [selectedArea, propertyStatus, propertyType, priceRange, bedrooms, activeKeyword, sortBy, searchParams]);

  const getPriceParams = (range, status) => {
    if (!range) return { priceMin: null, priceMax: null };
    
    const isRent = status === 'FOR RENT';
    if (isRent) {
      switch (range) {
        case 'under_1500': return { priceMin: null, priceMax: 1500 };
        case '1500_3000': return { priceMin: 1500, priceMax: 3000 };
        case 'above_3000': return { priceMin: 3000, priceMax: null };
        default: return { priceMin: null, priceMax: null };
      }
    } else {
      switch (range) {
        case 'under_500k': return { priceMin: null, priceMax: 500000 };
        case '500k_1m': return { priceMin: 500000, priceMax: 1000000 };
        case '1m_1.5m': return { priceMin: 1000000, priceMax: 1500000 };
        case '1.5m_2m': return { priceMin: 1500000, priceMax: 2000000 };
        case 'above_2m': return { priceMin: 2000000, priceMax: null };
        default: return { priceMin: null, priceMax: null };
      }
    }
  };

  const fetchProperties = async (pageNum, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const priceParams = getPriceParams(priceRange, propertyStatus);
      const isPlaceholder = (val) => !val || val.trim() === '' || val === 'Property Type' || val === 'Bedrooms' || val === 'All';

      const params = {
        area: selectedArea && selectedArea.trim() !== '' ? selectedArea : null,
        propertyType: isPlaceholder(propertyType) ? null : propertyType,
        bedrooms: isPlaceholder(bedrooms) ? null : bedrooms,
        priceMin: priceParams.priceMin,
        priceMax: priceParams.priceMax,
        status: propertyStatus,
        keyword: activeKeyword && activeKeyword.trim() !== '' ? activeKeyword.trim() : null,
        sortBy,
        page: pageNum,
        size: 12
      };

      const res = await ListingService.searchProperties(params);
      
      if (append) {
        setListings(prev => [...prev, ...(res.listings || [])]);
      } else {
        setListings(res.listings || []);
      }
      
      setTotalCount(res.totalCount || 0);
      setPage(res.page || 0);
      setTotalPages(res.totalPages || 0);
    } catch (err) {
      console.error("Failed to fetch properties:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    setActiveKeyword(trimmed);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('page');
      if (trimmed) {
        p.set('keyword', trimmed);
      } else {
        p.delete('keyword');
        p.delete('q');
      }
      return p;
    }, { replace: true });
  };

  const handleClearKeyword = () => {
    setKeyword('');
    setActiveKeyword('');
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('page');
      p.delete('keyword');
      p.delete('q');
      return p;
    }, { replace: true });
  };

  const handleLoadMore = () => {
    if (page + 1 < totalPages) {
      fetchProperties(page + 1, true);
    }
  };

  const handlePageChange = (pageNum) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (pageNum === 0) {
        p.delete('page');
      } else {
        p.set('page', (pageNum + 1).toString());
      }
      return p;
    }, { replace: true });
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const getPropertyWhatsappUrl = (prop) => {
    const text = `Hi Nicole, I'm interested in the property listing #${prop.id}: ${prop.title} in ${prop.area} (${prop.price}). Could you please share more details?`;
    return `https://wa.me/${agent?.whatsappNumber || '60163288120'}?text=${encodeURIComponent(text)}`;
  };

  const agentWhatsappUrl = `https://wa.me/${agent?.whatsappNumber || '60163288120'}?text=${encodeURIComponent("Hi Nicole, I need help finding a property in Johor Bahru")}`;

  const currentSelectedAreaObj = (areas || []).find(
    a => a.title.toLowerCase() === (selectedArea || '').toLowerCase()
  );

  return (
    <div className="area-listing-page">
      {/* Hero Banner Section */}
      <div 
        className="area-hero-section" 
        style={{ backgroundImage: `linear-gradient(to right, rgba(9, 13, 22, 0.9) 30%, rgba(9, 13, 22, 0.4) 100%), url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80')` }}
      >
        <div className="area-hero-content">
          <span className="area-hero-tag">Search • Compare • Invest</span>
          <h1 className="area-hero-title">ALL PROPERTY LISTINGS</h1>
          <p className="area-hero-desc">
            Explore our comprehensive portfolio of residential homes, luxury condominiums, commercial units, and investment opportunities across Johor Bahru.
          </p>
        </div>
      </div>

      <div className="area-container">
        {/* Breadcrumbs */}
        <nav className="breadcrumbs" aria-label="breadcrumb">
          <span className="breadcrumb-item" onClick={onGoBack}>Home</span>
          <span className="breadcrumb-separator"><i className="bi bi-chevron-right"></i></span>
          <span className="breadcrumb-item active" aria-current="page">All Listings</span>
        </nav>

        {/* Page Title Header */}
        <div className="area-page-header">
          <div className="header-left">
            <h2 className="properties-section-title">
              {selectedArea ? `Properties In ${selectedArea}` : 'All Popular Properties in Johor.'}
            </h2>
            <p className="properties-section-subtitle">
              Filter by precinct, status, property type, and price range to find your ideal property.
            </p>
            <span className="properties-found-count">{totalCount} Properties Found</span>
          </div>
          {currentSelectedAreaObj && (
            <div className="header-right">
              <button className="btn-view-area-guide" onClick={() => setShowAreaGuideModal(true)}>VIEW AREA GUIDE</button>
            </div>
          )}
        </div>

        {/* Filter Form */}
        <form className="properties-filter-form" onSubmit={handleFilterSubmit}>
          {/* Keyword Search Bar */}
          <div className="filter-search-bar">
            <div className="search-input-group">
              <i className="bi bi-search search-icon"></i>
              <input 
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Search properties by keyword, project, area, road, or ID..."
                className="filter-search-input"
              />
              {keyword && (
                <button 
                  type="button" 
                  className="btn-clear-search" 
                  onClick={handleClearKeyword}
                  title="Clear search"
                >
                  <i className="bi bi-x-circle-fill"></i>
                </button>
              )}
            </div>
            <button type="submit" className="btn-filter-submit">
              SEARCH <i className="bi bi-search"></i>
            </button>
          </div>

          <div className="filter-grid">
            {/* Status Filter */}
            <div className="filter-select-wrapper">
              <select 
                value={propertyStatus} 
                onChange={(e) => {
                  setPropertyStatus(e.target.value);
                  setPriceRange('');
                }}
                className="filter-select"
              >
                <option value="FOR SALE">FOR SALE</option>
                <option value="FOR RENT">FOR RENT</option>
              </select>
            </div>

            {/* Area Filter (Specific to Popular Areas only) */}
            <div className="filter-select-wrapper">
              <select 
                value={selectedArea} 
                onChange={(e) => setSelectedArea(e.target.value)}
                className="filter-select"
              >
                <option value="">All Popular Areas</option>
                {areas && areas.map((a) => (
                  <option key={a.id} value={a.title}>{a.title}</option>
                ))}
              </select>
            </div>

            {/* Property Type Filter */}
            <div className="filter-select-wrapper">
              <select 
                value={propertyType} 
                onChange={(e) => setPropertyType(e.target.value)}
                className="filter-select"
              >
                <option value="">Property Type</option>
                <option value="Terrace House">Terrace House</option>
                <option value="Condo">Condo/Apartment</option>
                <option value="Semi-D">Semi-D</option>
                <option value="Cluster House">Cluster House</option>
                <option value="Bungalow">Bungalow</option>
                <option value="Shop/Office">Shop/Office</option>
              </select>
            </div>

            {/* Price Range Filter */}
            <div className="filter-select-wrapper">
              <select 
                value={priceRange} 
                onChange={(e) => setPriceRange(e.target.value)}
                className="filter-select"
              >
                <option value="">Price Range</option>
                {propertyStatus === 'FOR RENT' ? (
                  <>
                    <option value="under_1500">&lt; RM 1,500</option>
                    <option value="1500_3000">RM 1,500 - RM 3,000</option>
                    <option value="above_3000">&gt; RM 3,000</option>
                  </>
                ) : (
                  <>
                    <option value="under_500k">&lt; RM 500k</option>
                    <option value="500k_1m">RM 500k - RM 1M</option>
                    <option value="1m_1.5m">RM 1M - RM 1.5M</option>
                    <option value="1.5m_2m">RM 1.5M - RM 2M</option>
                    <option value="above_2m">&gt; RM 2M</option>
                  </>
                )}
              </select>
            </div>

            {/* Bedrooms Filter */}
            <div className="filter-select-wrapper">
              <select 
                value={bedrooms} 
                onChange={(e) => setBedrooms(e.target.value)}
                className="filter-select"
              >
                <option value="">Bedrooms</option>
                <option value="1">1 Bedroom</option>
                <option value="2">2 Bedrooms</option>
                <option value="3">3 Bedrooms</option>
                <option value="4">4 Bedrooms</option>
                <option value="5+">5+ Bedrooms</option>
              </select>
            </div>

            {(selectedArea || propertyType || priceRange || bedrooms || activeKeyword) && (
              <button 
                type="button" 
                className="btn-filter-reset"
                onClick={() => {
                  setSelectedArea('');
                  setPropertyType('');
                  setPriceRange('');
                  setBedrooms('');
                  setKeyword('');
                  setActiveKeyword('');
                  setSearchParams(prev => {
                    const p = new URLSearchParams(prev);
                    p.delete('page');
                    p.delete('keyword');
                    p.delete('q');
                    return p;
                  }, { replace: true });
                }}
              >
                RESET <i className="bi bi-arrow-counterclockwise"></i>
              </button>
            )}
          </div>

          <div className="sort-controls">
            <label htmlFor="sortBySelect" className="sort-label">Sort By:</label>
            <select 
              id="sortBySelect"
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="latest">Latest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
        </form>

        {/* Main Content Layout */}
        <div className="listings-layout-grid">
          {/* Left Column: Properties Listings */}
          <div className="listings-left-column">
            {loading ? (
              <div className="listings-loading-state">
                <i className="bi bi-arrow-repeat spin"></i> Loading property listings...
              </div>
            ) : listings.length === 0 ? (
              <div className="listings-empty-state">
                <i className="bi bi-info-circle"></i> No properties match your criteria. Try resetting or adjusting your search filters.
              </div>
            ) : (
              <>
                {/* Desktop Grid Layout */}
                <div className="properties-desktop-grid">
                  {listings.map((prop) => (
                    <div 
                      className="prop-listing-card" 
                      key={prop.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onSelectProperty && onSelectProperty(prop)}
                    >
                      <div className="prop-image-wrapper">
                        <span className={`prop-badge ${prop.listingType === 'FOR RENT' ? 'rent' : 'sale'}`}>
                          {prop.listingType}
                        </span>
                        <img src={prop.imageUrl} alt={prop.title} className="prop-image" />
                      </div>
                      <div className="prop-card-body">
                        <h3 className="prop-price">{prop.price}</h3>
                        <h4 className="prop-title" title={prop.title}>{prop.title}</h4>
                        <p className="prop-location" title={prop.area}>
                          <i className="bi bi-geo-alt"></i> <span>{prop.area}</span>
                        </p>
                        {prop.date && (
                          <p className="prop-date">
                            <i className="bi bi-clock"></i> <span>Listed on {prop.date}</span>
                          </p>
                        )}
                        
                        <div className="prop-icons-row">
                          <div className="prop-icon-item">
                            <i className="bi bi-door-open"></i> <span>{prop.bedrooms}</span>
                          </div>
                          <div className="prop-icon-item">
                            <i className="bi bi-water"></i> <span>{prop.bathrooms}</span>
                          </div>
                          <div className="prop-icon-item size">
                            <i className="bi bi-arrows-angle-expand"></i> <span>{prop.size}</span>
                          </div>
                        </div>

                        <div className="prop-card-actions">
                          <button 
                            className="btn-prop-view-details"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectProperty && onSelectProperty(prop);
                            }}
                          >
                            VIEW DETAILS
                          </button>
                          <a 
                            href={getPropertyWhatsappUrl(prop)} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn-prop-whatsapp"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="bi bi-whatsapp"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile List Layout */}
                <div className="properties-mobile-list">
                  {listings.map((prop) => (
                    <div 
                      className="prop-mobile-row" 
                      key={prop.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onSelectProperty && onSelectProperty(prop)}
                    >
                      <div className="prop-mobile-img-col">
                        <span className={`prop-badge ${prop.listingType === 'FOR RENT' ? 'rent' : 'sale'}`}>
                          {prop.listingType}
                        </span>
                        <img src={prop.imageUrl} alt={prop.title} className="prop-mobile-img" />
                      </div>
                      <div className="prop-mobile-info-col">
                        <div className="prop-mobile-header">
                          <h3 className="prop-mobile-price">{prop.price}</h3>
                          <a 
                            href={getPropertyWhatsappUrl(prop)} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn-prop-whatsapp-mobile"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="bi bi-whatsapp"></i>
                          </a>
                        </div>
                        <h4 className="prop-mobile-title" title={prop.title}>{prop.title}</h4>
                        <p className="prop-mobile-location" title={prop.area}>
                          <i className="bi bi-geo-alt"></i> <span>{prop.area}</span>
                        </p>
                        {prop.date && (
                          <p className="prop-mobile-date">
                            <i className="bi bi-clock"></i> <span>Listed on {prop.date}</span>
                          </p>
                        )}
                        
                        <div className="prop-mobile-icons">
                          <span><i className="bi bi-door-open"></i> {prop.bedrooms}</span>
                          <span><i className="bi bi-water"></i> {prop.bathrooms}</span>
                          <span><i className="bi bi-arrows-angle-expand"></i> {prop.size}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Load More Button for Mobile */}
                  {page + 1 < totalPages && (
                    <button 
                      onClick={handleLoadMore} 
                      className="btn-load-more"
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <><i className="bi bi-arrow-repeat spin"></i> LOADING...</>
                      ) : (
                        "LOAD MORE PROPERTIES"
                      )}
                    </button>
                  )}
                </div>

                {/* Desktop Pagination */}
                {totalPages > 1 && (
                  <div className="desktop-pagination">
                    <button 
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      className="pagination-btn arrow"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    
                    {[...Array(totalPages)].map((_, idx) => {
                      if (idx === 0 || idx === totalPages - 1 || (idx >= page - 2 && idx <= page + 2)) {
                        return (
                          <button
                            key={idx}
                            onClick={() => handlePageChange(idx)}
                            className={`pagination-btn ${page === idx ? 'active' : ''}`}
                          >
                            {idx + 1}
                          </button>
                        );
                      }
                      if (idx === 1 || idx === totalPages - 2) {
                        return <span className="pagination-ellipsis" key={idx}>...</span>;
                      }
                      return null;
                    })}

                    <button 
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages - 1}
                      className="pagination-btn arrow"
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Column: Sidebar Widgets */}
          <div className="listings-right-sidebar">
            {/* Quick Area Jump Widget */}
            <div className="sidebar-widget about-widget">
              <h3 className="widget-title">Popular Precincts</h3>
              <p className="widget-about-text">Select an area below to filter property listings by precinct:</p>
              
              <ul className="widget-checklist">
                {(areas || []).map((a) => (
                  <li 
                    key={a.id} 
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedArea(a.title);
                      window.scrollTo({ top: 400, behavior: 'smooth' });
                    }}
                  >
                    <i className={`bi ${selectedArea === a.title ? 'bi-check-circle-fill' : 'bi-geo-alt-fill'}`} style={{ color: selectedArea === a.title ? 'var(--gold-hover)' : 'inherit' }}></i>
                    <span style={{ fontWeight: selectedArea === a.title ? '700' : 'normal', color: selectedArea === a.title ? 'var(--gold-hover)' : 'inherit' }}>
                      {a.title}
                    </span>
                  </li>
                ))}
              </ul>

              {selectedArea && (
                <button 
                  className="btn-widget-area-guide" 
                  onClick={() => setSelectedArea('')}
                  style={{ background: 'transparent', border: '1px solid #cbd5e1', color: 'var(--text-dark)', marginTop: '0.75rem' }}
                >
                  SHOW ALL AREAS
                </button>
              )}
            </div>

            {/* Need Help Finding Widget */}
            <div className="sidebar-widget help-widget">
              <h3 className="widget-title">Need Help Finding?</h3>
              <p className="widget-help-text">Looking for a specific home or commercial investment in Johor Bahru?</p>
              
              <div className="agent-profile-row">
                <img 
                  src={ListingService.formatImageUrl(agent?.photoUrl || "/api/app-images/wa_profile.jpg")} 
                  alt={agent?.name || "Nicole Pua"} 
                  className="widget-agent-photo" 
                />
                <div className="widget-agent-info">
                  <h4 className="widget-agent-name">{agent?.name || "Nicole Pua"}</h4>
                  <p className="widget-agent-title">{agent?.title || "Property Agent"}</p>
                  <p className="widget-agent-phone"><i className="bi bi-telephone"></i> +6016-3288 120</p>
                </div>
              </div>

              <a 
                href={agentWhatsappUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-widget-whatsapp"
              >
                <i className="bi bi-whatsapp"></i> WHATSAPP ME
              </a>
            </div>
          </div>
        </div>
      </div>

      {showAreaGuideModal && currentSelectedAreaObj && (
        <AreaGuideModal 
          area={currentSelectedAreaObj} 
          agent={agent} 
          onClose={() => setShowAreaGuideModal(false)} 
        />
      )}
    </div>
  );
};

export default AllListingsPage;
